import { A, useNavigate, useParams, useSearchParams } from '@solidjs/router';
import { type Component, createEffect, createMemo, createResource, createSignal, For, Show } from 'solid-js';
import {
  type Artifact,
  createProjectInteractionRequirement,
  deleteProject,
  deleteProjectInteractionRequirement,
  deleteProjectWorkflow,
  deleteRequirement,
  executeProject,
  fetchAgents,
  fetchExecutions,
  fetchFiles,
  fetchInteractions,
  fetchInteractionTypes,
  fetchMyArtifacts,
  fetchProject,
  fetchProjectInteractionRequirements,
  fetchProjectWorkflows,
  fetchRequirements,
  fetchSkills,
  type FileInfo,
  type Interaction,
  type InteractionType,
  type ProjectInteractionRequirement,
  type ProjectWorkflow,
  type Requirement,
} from '../services/api';
import { FileList, FileUploader } from '../components/Files';

type TabType = 'overview' | 'requirements' | 'workflows' | 'agents' | 'executions' | 'artifacts' | 'files' | 'interactions';

const validTabs: TabType[] = ['overview', 'requirements', 'workflows', 'agents', 'executions', 'artifacts', 'files', 'interactions'];

const ProjectDetail: Component = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read initial tab from URL parameter
  const getInitialTab = (): TabType => {
    const tabParam = searchParams.tab as string;
    if (tabParam && validTabs.includes(tabParam as TabType)) {
      return tabParam as TabType;
    }
    return 'overview';
  };

  // UI state
  const [activeTab, setActiveTab] = createSignal<TabType>(getInitialTab());
  const [showDeleteDialog, setShowDeleteDialog] = createSignal(false);
  const [isDeleting, setIsDeleting] = createSignal(false);
  const [isRunning, setIsRunning] = createSignal(false);
  const [runError, setRunError] = createSignal<string | null>(null);
  const [showRunDialog, setShowRunDialog] = createSignal(false);
  const [runInput, setRunInput] = createSignal('');
  const [runResult, setRunResult] = createSignal<unknown>(null);

  // HITL requirement form state
  const [showAddRequirementForm, setShowAddRequirementForm] = createSignal(false);
  const [newRequirementType, setNewRequirementType] = createSignal('');
  const [newRequirementTrigger, setNewRequirementTrigger] = createSignal<'phase' | 'event' | 'manual' | 'always'>('manual');
  const [newRequirementPriority, setNewRequirementPriority] = createSignal<'low' | 'normal' | 'high' | 'urgent'>('normal');

  // Fetch project data
  // Note: We wrap fetchProject to avoid SolidJS passing { value, refetching } as the retries parameter
  const [project, { refetch: _refetchProject }] = createResource(
    () => params.id,
    (projectId) => fetchProject(projectId),
  );

  // Fetch agents for this project
  const [agentsData] = createResource(
    () => params.id,
    async (projectId) => {
      return fetchAgents({ projectId, limit: 50 });
    },
  );

  // Fetch recent executions for this project
  const [executionsData, { refetch: refetchExecutions }] = createResource(
    () => params.id,
    async (projectId) => {
      return fetchExecutions({ projectId, limit: 10 });
    },
  );

  // Fetch requirements for this project
  const [requirementsData, { refetch: refetchRequirements }] = createResource(
    () => params.id,
    async (projectId) => {
      return fetchRequirements({ projectId, limit: 50 });
    },
  );

  // Fetch workflows for this project
  const [workflowsData, { refetch: refetchWorkflows }] = createResource(
    () => params.id,
    async (projectId) => {
      return fetchProjectWorkflows({ projectId, limit: 50 });
    },
  );

  // Fetch interactions for this project
  const [interactionsData] = createResource(
    () => params.id,
    async (projectId) => {
      return fetchInteractions({ projectId, limit: 50 });
    },
  );

  // Fetch files for this project
  const [filesData, { refetch: refetchFiles }] = createResource(
    () => params.id,
    async (projectId) => {
      return fetchFiles('project', projectId);
    },
  );

  // Fetch artifacts for this project
  const [artifactsData, { refetch: refetchArtifacts }] = createResource(
    () => params.id,
    async (projectId) => {
      return fetchMyArtifacts({ projectId, limit: 100 });
    },
  );

  // Fetch interaction types (for HITL settings)
  const [interactionTypesData] = createResource(async () => {
    return fetchInteractionTypes();
  });

  // Fetch project interaction requirements (HITL settings)
  const [requirementsConfigData, { refetch: refetchRequirementsConfig }] = createResource(
    () => params.id,
    async (projectId) => {
      return fetchProjectInteractionRequirements(projectId);
    },
  );

  // Fetch skills (for display purposes)
  const [skillsData] = createResource(async () => {
    return fetchSkills({ limit: 50 });
  });

  const agents = createMemo(() => agentsData()?.items || []);
  const executions = createMemo(() => executionsData()?.items || []);
  const requirements = createMemo(() => requirementsData()?.items || []);
  const workflows = createMemo(() => workflowsData()?.items || []);
  const interactions = createMemo(() => interactionsData()?.items || []);
  const interactionTypes = createMemo(() => interactionTypesData() || []);
  const requirementsConfig = createMemo(() => requirementsConfigData()?.data || []);
  const files = createMemo(() => filesData()?.files || []);
  const artifacts = createMemo(() => artifactsData()?.items || []);
  const _skills = createMemo(() => skillsData()?.items || []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'draft':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'paused':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'archived':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'running':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'failed':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'cancelled':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getVisibilityBadgeClass = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'group':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'private':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getRunModeBadge = (runMode: string) => {
    switch (runMode) {
      case 'buy':
        return {
          label: 'Buy',
          class: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
        };
      case 'run':
        return {
          label: 'Run',
          class: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
        };
      case 'both':
        return {
          label: 'Buy & Run',
          class: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
        };
      default:
        return {
          label: runMode,
          class: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        };
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getTriggerTypeBadge = (type: string) => {
    switch (type) {
      case 'manual':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'scheduled':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'webhook':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'event':
        return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const handleDeleteRequirement = async (id: string) => {
    if (!confirm('Are you sure you want to delete this requirement?')) return;
    try {
      await deleteRequirement(id);
      refetchRequirements();
    } catch (error) {
      console.error('Failed to delete requirement:', error);
    }
  };

  const handleDeleteWorkflow = async (id: string) => {
    if (!confirm('Are you sure you want to delete this workflow?')) return;
    try {
      await deleteProjectWorkflow(id);
      refetchWorkflows();
    } catch (error) {
      console.error('Failed to delete workflow:', error);
    }
  };

  const handleDeleteInteractionRequirement = async (id: string) => {
    if (!confirm('Are you sure you want to delete this HITL requirement?')) return;
    try {
      await deleteProjectInteractionRequirement(params.id, id);
      refetchRequirementsConfig();
    } catch (error) {
      console.error('Failed to delete interaction requirement:', error);
    }
  };

  const handleAddInteractionRequirement = async (
    interactionTypeId: string,
    triggerType: 'phase' | 'event' | 'manual' | 'always',
    priority: 'low' | 'normal' | 'high' | 'urgent',
  ) => {
    try {
      await createProjectInteractionRequirement(params.id, {
        interactionTypeId,
        triggerType,
        priority,
        required: true,
      });
      refetchRequirementsConfig();
    } catch (error) {
      console.error('Failed to add interaction requirement:', error);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProject(params.id);
      navigate('/projects');
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRunProject = () => {
    setRunError(null);
    setRunResult(null);
    setShowRunDialog(true);
  };

  const handleExecuteProject = async () => {
    const p = project();
    const hasInputSchema = p?.input_schema && Object.keys(p.input_schema).length > 0;

    // Only require input if there's an input_schema (context fields)
    // Otherwise, backend will use project.goal as default
    if (hasInputSchema && !runInput().trim()) {
      setRunError('Please provide the required context');
      return;
    }

    setIsRunning(true);
    setRunError(null);
    try {
      // If user provided input, send it; otherwise send empty (backend uses project.goal)
      const params_exec: { input?: string; context?: Record<string, unknown> } = {};
      if (runInput().trim()) {
        params_exec.input = runInput();
      }

      const result = await executeProject(params.id, params_exec);
      setRunResult(result);
      // Navigate to execution detail page so user can monitor progress
      if (result.executionId) {
        setShowRunDialog(false);
        setRunInput('');
        navigate(`/executions/${result.executionId}`);
      }
    } catch (error) {
      console.error('Failed to run project:', error);
      setRunError(error instanceof Error ? error.message : 'Failed to run project');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div>
      {/* Delete Confirmation Dialog */}
      <Show when={showDeleteDialog()}>
        <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-gray-800 rounded-lg p-6 max-w-md text-center border border-gray-700">
            <div class="mb-4 flex justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <h2 class="text-xl font-medium mb-4">Delete Project</h2>
            <p class="text-gray-400 mb-6">
              Are you sure you want to delete "{project()?.name}"? This will also remove all
              associated agents and executions. This action cannot be undone.
            </p>
            <div class="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting()}
                class="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting()}
                class="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50"
              >
                {isDeleting() ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Run Project Dialog */}
      <Show when={showRunDialog()}>
        <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 border border-gray-700">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-xl font-medium text-gray-200 flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-green-400"
                >
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Run Project
              </h2>
              <button
                type="button"
                onClick={() => setShowRunDialog(false)}
                class="text-gray-400 hover:text-gray-200 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Show project goal as the default execution context */}
            <div class="mb-4 p-3 rounded-lg bg-gray-900 border border-gray-700">
              <p class="text-xs text-gray-500 mb-1">Project Goal</p>
              <p class="text-gray-300 text-sm">{project()?.goal || 'No goal defined'}</p>
            </div>

            {/* Optional: Custom prompt override */}
            <details class="mb-4">
              <summary class="text-gray-400 text-sm cursor-pointer hover:text-gray-300">
                Add custom instructions (optional)
              </summary>
              <div class="mt-2">
                <textarea
                  value={runInput()}
                  onInput={(e) => setRunInput(e.currentTarget.value)}
                  placeholder="Override or add to the project goal with specific instructions..."
                  rows={3}
                  class="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors resize-none"
                />
              </div>
            </details>

            <Show when={runError()}>
              <div class="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
                {runError()}
              </div>
            </Show>

            <Show when={runResult()}>
              <div class="mb-4 p-3 rounded-lg bg-green-500/20 border border-green-500/30 text-green-400 text-sm">
                Execution started successfully!
              </div>
            </Show>

            <div class="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowRunDialog(false)}
                disabled={isRunning()}
                class="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteProject}
                disabled={isRunning()}
                class="px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all disabled:opacity-50"
              >
                {isRunning() ? (
                  <span class="flex items-center gap-2">
                    <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Running...
                  </span>
                ) : (
                  'Run Project'
                )}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Loading State */}
      <Show when={project.loading}>
        <div class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
        </div>
      </Show>

      {/* Error State */}
      <Show when={project.error}>
        <div class="text-center py-12">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mx-auto text-red-400 mb-4"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h3 class="text-xl font-medium text-gray-300 mb-2">Project not found</h3>
          <p class="text-gray-500 mb-6">
            The project you're looking for doesn't exist or you don't have access to it.
          </p>
          <A
            href="/projects"
            class="inline-flex items-center px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
          >
            Back to Projects
          </A>
        </div>
      </Show>

      {/* Project Content */}
      <Show when={!project.loading && !project.error && project()}>
        {(proj) => (
          <>
            {/* Header */}
            <div class="mb-6">
              <div class="flex items-center gap-4 mb-4">
                <A
                  href="/projects"
                  class="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m12 19-7-7 7-7" />
                    <path d="M19 12H5" />
                  </svg>
                </A>
                <div class="flex-1">
                  <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                    {proj().name}
                  </h1>
                  <p class="text-gray-400 text-sm mt-1">{proj().goal}</p>
                </div>
                <div class="flex items-center gap-2">
                  {/* Run button - always visible, disabled if not active */}
                  <div class="relative group">
                    <button
                      type="button"
                      onClick={handleRunProject}
                      disabled={isRunning() || proj().status !== 'active'}
                      class={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                        proj().status === 'active'
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white'
                          : 'bg-gray-700 text-gray-400'
                      }`}
                    >
                      {isRunning() ? (
                        <span class="flex items-center gap-2">
                          <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            />
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Running...
                        </span>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="mr-2"
                          >
                            <polygon points="5 3 19 12 5 21 5 3" />
                          </svg>
                          Run Project
                        </>
                      )}
                    </button>
                    {/* Tooltip for non-active projects */}
                    <Show when={proj().status !== 'active'}>
                      <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-gray-300 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none border border-gray-700">
                        Set project status to "Active" to run
                      </div>
                    </Show>
                  </div>
                  <A
                    href={`/projects/${params.id}/edit`}
                    class="inline-flex items-center px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="mr-2"
                    >
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                    Edit
                  </A>
                  <button
                    type="button"
                    onClick={() => setShowDeleteDialog(true)}
                    class="p-2 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                    title="Delete project"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M3 6h18" />
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Status badges */}
              <div class="flex flex-wrap gap-2">
                <span
                  class={`px-2 py-1 rounded-full text-xs border ${getStatusBadgeClass(
                    proj().status,
                  )}`}
                >
                  {proj().status}
                </span>
                <span
                  class={`px-2 py-1 rounded-full text-xs border ${getVisibilityBadgeClass(
                    proj().visibility,
                  )}`}
                >
                  {proj().visibility}
                </span>
                <span
                  class={`px-2 py-1 rounded-full text-xs border ${
                    getRunModeBadge(proj().run_mode).class
                  }`}
                >
                  {getRunModeBadge(proj().run_mode).label}
                </span>
                <span class="px-2 py-1 rounded-full text-xs bg-gray-700/50 text-gray-400 border border-gray-600">
                  v{proj().version}
                </span>
              </div>
            </div>

            {/* Run Error */}
            <Show when={runError()}>
              <div class="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400">
                {runError()}
              </div>
            </Show>

            {/* Tab Navigation */}
            <div class="mb-6 border-b border-gray-700">
              <nav class="flex gap-6 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setActiveTab('overview')}
                  class={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                    activeTab() === 'overview'
                      ? 'text-purple-400 border-purple-400'
                      : 'text-gray-400 hover:text-gray-200 border-transparent'
                  }`}
                >
                  Overview
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('files')}
                  class={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${
                    activeTab() === 'files'
                      ? 'text-purple-400 border-purple-400'
                      : 'text-gray-400 hover:text-gray-200 border-transparent'
                  }`}
                >
                  Files
                  <span class="px-1.5 py-0.5 rounded bg-gray-700 text-xs">
                    {filesData()?.files?.length || 0}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('requirements')}
                  class={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${
                    activeTab() === 'requirements'
                      ? 'text-purple-400 border-purple-400'
                      : 'text-gray-400 hover:text-gray-200 border-transparent'
                  }`}
                >
                  Requirements
                  <span class="px-1.5 py-0.5 rounded bg-gray-700 text-xs">
                    {requirements().length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('workflows')}
                  class={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${
                    activeTab() === 'workflows'
                      ? 'text-purple-400 border-purple-400'
                      : 'text-gray-400 hover:text-gray-200 border-transparent'
                  }`}
                >
                  Workflows
                  <span class="px-1.5 py-0.5 rounded bg-gray-700 text-xs">
                    {workflows().length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('agents')}
                  class={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${
                    activeTab() === 'agents'
                      ? 'text-purple-400 border-purple-400'
                      : 'text-gray-400 hover:text-gray-200 border-transparent'
                  }`}
                >
                  Agents
                  <span class="px-1.5 py-0.5 rounded bg-gray-700 text-xs">{agents().length}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('executions')}
                  class={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${
                    activeTab() === 'executions'
                      ? 'text-purple-400 border-purple-400'
                      : 'text-gray-400 hover:text-gray-200 border-transparent'
                  }`}
                >
                  Executions
                  <span class="px-1.5 py-0.5 rounded bg-gray-700 text-xs">
                    {executions().length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('interactions')}
                  class={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${
                    activeTab() === 'interactions'
                      ? 'text-purple-400 border-purple-400'
                      : 'text-gray-400 hover:text-gray-200 border-transparent'
                  }`}
                >
                  Interactions
                  <Show when={interactions().filter((i) => i.status === 'pending').length > 0}>
                    <span class="px-1.5 py-0.5 rounded bg-orange-500/30 text-orange-400 text-xs">
                      {interactions().filter((i) => i.status === 'pending').length}
                    </span>
                  </Show>
                  <Show when={interactions().filter((i) => i.status === 'pending').length === 0}>
                    <span class="px-1.5 py-0.5 rounded bg-gray-700 text-xs">
                      {interactions().length}
                    </span>
                  </Show>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('artifacts')}
                  class={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${
                    activeTab() === 'artifacts'
                      ? 'text-purple-400 border-purple-400'
                      : 'text-gray-400 hover:text-gray-200 border-transparent'
                  }`}
                >
                  Artifacts
                  <span class="px-1.5 py-0.5 rounded bg-gray-700 text-xs">
                    {artifacts().length}
                  </span>
                </button>
              </nav>
            </div>

            {/* Overview Tab */}
            <Show when={activeTab() === 'overview'}>
              <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Info */}
                <div class="lg:col-span-2 space-y-6">
                  {/* Vision */}
                  <Show when={proj().vision?.mission || proj().vision?.problem}>
                    <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                      <h2 class="text-lg font-medium text-gray-200 mb-4 flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="text-blue-400"
                        >
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        Vision
                      </h2>
                      <div class="space-y-4">
                        <Show when={proj().vision?.mission}>
                          <div>
                            <h3 class="text-sm font-medium text-gray-400 mb-1">Mission</h3>
                            <p class="text-gray-200">{proj().vision?.mission}</p>
                          </div>
                        </Show>
                        <Show when={proj().vision?.problem}>
                          <div>
                            <h3 class="text-sm font-medium text-gray-400 mb-1">
                              Problem Being Solved
                            </h3>
                            <p class="text-gray-200">{proj().vision?.problem}</p>
                          </div>
                        </Show>
                      </div>
                    </section>
                  </Show>

                  {/* Agents Summary */}
                  <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                    <div class="flex items-center justify-between mb-4">
                      <h2 class="text-lg font-medium text-gray-200 flex items-center gap-2">
                        <svg
                          class="w-5 h-5 text-purple-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12h.01M15 12h.01M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5"
                          />
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1"
                          />
                        </svg>
                        Agents ({agents().length})
                      </h2>
                      <A
                        href={`/projects/${params.id}/agents/new`}
                        class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        + Add Agent
                      </A>
                    </div>

                    <Show when={agents().length === 0}>
                      <div class="text-center py-8 text-gray-500">
                        <p>No agents assigned to this project yet.</p>
                        <A
                          href={`/projects/${params.id}/agents/new`}
                          class="inline-block mt-2 text-purple-400 hover:text-purple-300"
                        >
                          Create your first agent
                        </A>
                      </div>
                    </Show>

                    <Show when={agents().length > 0}>
                      <div class="space-y-3">
                        <For each={agents().slice(0, 5)}>
                          {(agent) => (
                            <A
                              href={`/projects/${params.id}/agents/${agent.id}/edit`}
                              class="flex items-center gap-3 p-3 rounded-lg bg-gray-900/50 hover:bg-gray-900 transition-colors"
                            >
                              <div class="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400">
                                <svg
                                  class="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M9 12h.01M15 12h.01M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5"
                                  />
                                </svg>
                              </div>
                              <div class="flex-1 min-w-0">
                                <h3 class="text-sm font-medium text-gray-200 truncate">
                                  {agent.name}
                                </h3>
                                <p class="text-xs text-gray-500 truncate">
                                  {agent.purpose || 'No purpose defined'}
                                </p>
                              </div>
                              <span
                                class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                                  agent.status,
                                )}`}
                              >
                                {agent.status}
                              </span>
                            </A>
                          )}
                        </For>
                        <Show when={agents().length > 5}>
                          <button
                            type="button"
                            onClick={() => setActiveTab('agents')}
                            class="w-full text-center py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
                          >
                            View all {agents().length} agents
                          </button>
                        </Show>
                      </div>
                    </Show>
                  </section>

                  {/* Recent Executions */}
                  <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                    <div class="flex items-center justify-between mb-4">
                      <h2 class="text-lg font-medium text-gray-200 flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="text-cyan-400"
                        >
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                        Recent Executions
                      </h2>
                      <button
                        type="button"
                        onClick={() => setActiveTab('executions')}
                        class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        View all
                      </button>
                    </div>

                    <Show when={executions().length === 0}>
                      <div class="text-center py-8 text-gray-500">
                        <p>No executions yet.</p>
                        <Show when={proj().status === 'active'}>
                          <button
                            type="button"
                            onClick={handleRunProject}
                            disabled={isRunning()}
                            class="inline-block mt-2 text-green-400 hover:text-green-300"
                          >
                            Run this project
                          </button>
                        </Show>
                      </div>
                    </Show>

                    <Show when={executions().length > 0}>
                      <div class="space-y-2">
                        <For each={executions().slice(0, 5)}>
                          {(execution) => (
                            <div class="flex items-center gap-3 p-3 rounded-lg bg-gray-900/50">
                              <span
                                class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                                  execution.status,
                                )}`}
                              >
                                {execution.status}
                              </span>
                              <div class="flex-1 min-w-0">
                                <p class="text-sm text-gray-300 truncate">
                                  {execution.current_step || 'Execution'}
                                </p>
                                <p class="text-xs text-gray-500">
                                  {formatRelativeTime(execution.created_at)}
                                </p>
                              </div>
                              <Show when={execution.tokens_used}>
                                <span class="text-xs text-gray-500">
                                  {execution.tokens_used?.toLocaleString()} tokens
                                </span>
                              </Show>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                  </section>
                </div>

                {/* Sidebar */}
                <div class="space-y-6">
                  {/* Pending Interactions Alert */}
                  <Show when={interactions().filter((i: Interaction) => i.status === 'pending').length > 0}>
                    <section class="bg-orange-500/10 rounded-lg border border-orange-500/30 p-6">
                      <div class="flex items-center gap-3 mb-3">
                        <div class="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="text-orange-400"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        </div>
                        <div>
                          <h2 class="text-lg font-medium text-orange-400">
                            {interactions().filter((i: Interaction) => i.status === 'pending').length} Pending{' '}
                            {interactions().filter((i: Interaction) => i.status === 'pending').length === 1 ? 'Interaction' : 'Interactions'}
                          </h2>
                          <p class="text-sm text-gray-400">Agents need your input</p>
                        </div>
                      </div>
                      <div class="space-y-2">
                        <For each={interactions().filter((i: Interaction) => i.status === 'pending').slice(0, 3)}>
                          {(interaction: Interaction) => (
                            <A
                              href={`/interactions?interactionId=${interaction.id}`}
                              class="flex items-center gap-2 p-2 rounded-lg bg-gray-900/50 hover:bg-gray-900 transition-colors"
                            >
                              <span class="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                              <span class="text-sm text-gray-200 truncate flex-1">{interaction.title}</span>
                              <span class={`px-1.5 py-0.5 rounded text-xs ${
                                interaction.priority === 'urgent'
                                  ? 'bg-red-500/20 text-red-400'
                                  : interaction.priority === 'high'
                                    ? 'bg-orange-500/20 text-orange-400'
                                    : 'bg-gray-700 text-gray-400'
                              }`}>
                                {interaction.priority}
                              </span>
                            </A>
                          )}
                        </For>
                        <Show when={interactions().filter((i: Interaction) => i.status === 'pending').length > 3}>
                          <button
                            type="button"
                            onClick={() => setActiveTab('interactions')}
                            class="w-full text-center py-2 text-sm text-orange-400 hover:text-orange-300 transition-colors"
                          >
                            View all {interactions().filter((i: Interaction) => i.status === 'pending').length} pending
                          </button>
                        </Show>
                      </div>
                    </section>
                  </Show>

                  {/* Quick Stats */}
                  <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-200 mb-4">Statistics</h2>
                    <div class="space-y-4">
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Agents</span>
                        <span class="text-gray-200 font-medium">{agents().length}</span>
                      </div>
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Total Executions</span>
                        <span class="text-gray-200 font-medium">
                          {executionsData()?.total || 0}
                        </span>
                      </div>
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Created</span>
                        <span class="text-gray-200 text-sm">{formatDate(proj().created_at)}</span>
                      </div>
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Updated</span>
                        <span class="text-gray-200 text-sm">{formatDate(proj().updated_at)}</span>
                      </div>
                    </div>
                  </section>

                  {/* Pricing Info */}
                  <Show when={proj().pricing}>
                    <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                      <h2 class="text-lg font-medium text-gray-200 mb-4">Pricing</h2>
                      <div class="space-y-3">
                        <div class="flex items-center justify-between">
                          <span class="text-gray-400">Type</span>
                          <span class="text-gray-200 capitalize">{proj().pricing?.type}</span>
                        </div>
                        <Show when={proj().pricing?.price}>
                          <div class="flex items-center justify-between">
                            <span class="text-gray-400">Price</span>
                            <span class="text-gray-200 font-medium">
                              {proj().pricing?.currency || 'EUR'} {proj().pricing?.price}
                            </span>
                          </div>
                        </Show>
                      </div>
                    </section>
                  </Show>

                  {/* Quick Actions */}
                  <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-200 mb-4">Quick Actions</h2>
                    <div class="space-y-2">
                      <A
                        href={`/projects/${params.id}/agents/new`}
                        class="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-900 text-gray-300 hover:text-gray-100 transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M12 5v14" />
                          <path d="M5 12h14" />
                        </svg>
                        Add Agent
                      </A>
                      <A
                        href={`/projects/${params.id}/edit`}
                        class="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-900 text-gray-300 hover:text-gray-100 transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                        </svg>
                        Edit Project
                      </A>
                      <A
                        href="/executions"
                        class="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-900 text-gray-300 hover:text-gray-100 transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M12 8v4l3 3" />
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                        Execution History
                      </A>
                    </div>
                  </section>
                </div>
              </div>
            </Show>

            {/* Requirements Tab */}
            <Show when={activeTab() === 'requirements'}>
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <p class="text-gray-400">
                    {requirements().length} requirement{requirements().length !== 1 ? 's' : ''} in
                    this project
                  </p>
                  <A
                    href={`/projects/${params.id}/requirements/new`}
                    class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium transition-all"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="mr-2"
                    >
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                    New Requirement
                  </A>
                </div>

                <Show when={requirements().length === 0}>
                  <div class="text-center py-12 bg-gray-800/50 rounded-lg border border-gray-700">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="mx-auto text-gray-600 mb-4"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                    <h3 class="text-xl font-medium text-gray-300 mb-2">No requirements yet</h3>
                    <p class="text-gray-500 mb-6">Define what your project needs to accomplish.</p>
                    <A
                      href={`/projects/${params.id}/requirements/new`}
                      class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium transition-all"
                    >
                      Create First Requirement
                    </A>
                  </div>
                </Show>

                <Show when={requirements().length > 0}>
                  <div class="space-y-3">
                    <For each={requirements()}>
                      {(req: Requirement) => (
                        <div class="bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-all p-4">
                          <div class="flex items-start justify-between">
                            <div class="flex-1 min-w-0">
                              <div class="flex items-center gap-3">
                                <A
                                  href={`/projects/${params.id}/requirements/${req.id}`}
                                  class="text-lg font-medium text-gray-200 hover:text-amber-400 transition-colors truncate"
                                >
                                  {req.name}
                                </A>
                                <span
                                  class={`px-2 py-0.5 rounded-full text-xs border ${getPriorityBadgeClass(
                                    req.priority,
                                  )}`}
                                >
                                  {req.priority}
                                </span>
                                <span
                                  class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                                    req.status,
                                  )}`}
                                >
                                  {req.status}
                                </span>
                              </div>
                              <Show when={req.description}>
                                <p class="text-gray-500 text-sm mt-1 line-clamp-2">
                                  {req.description}
                                </p>
                              </Show>
                              <div class="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <Show when={req.acceptanceCriteria?.length}>
                                  <span>
                                    {req.acceptanceCriteria.filter((c) => c.completed).length}/
                                    {req.acceptanceCriteria.length} criteria met
                                  </span>
                                </Show>
                                <Show when={req.dueDate}>
                                  <span>Due {new Date(req.dueDate!).toLocaleDateString()}</span>
                                </Show>
                                <span>Updated {formatRelativeTime(req.updatedAt)}</span>
                              </div>
                            </div>
                            <div class="flex items-center gap-2 ml-4">
                              <A
                                href={`/projects/${params.id}/requirements/${req.id}`}
                                class="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                              >
                                Edit
                              </A>
                              <button
                                type="button"
                                onClick={() => handleDeleteRequirement(req.id)}
                                class="text-sm text-red-400 hover:text-red-300 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            </Show>

            {/* Workflows Tab */}
            <Show when={activeTab() === 'workflows'}>
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <p class="text-gray-400">
                    {workflows().length} workflow{workflows().length !== 1 ? 's' : ''} in this
                    project
                  </p>
                  <A
                    href={`/projects/${params.id}/workflows/new`}
                    class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-medium transition-all"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="mr-2"
                    >
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                    New Workflow
                  </A>
                </div>

                <Show when={workflows().length === 0}>
                  <div class="text-center py-12 bg-gray-800/50 rounded-lg border border-gray-700">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="mx-auto text-gray-600 mb-4"
                    >
                      <polyline points="16 3 21 3 21 8" />
                      <line x1="4" y1="20" x2="21" y2="3" />
                      <polyline points="21 16 21 21 16 21" />
                      <line x1="15" y1="15" x2="21" y2="21" />
                      <line x1="4" y1="4" x2="9" y2="9" />
                    </svg>
                    <h3 class="text-xl font-medium text-gray-300 mb-2">No workflows yet</h3>
                    <p class="text-gray-500 mb-6">
                      Define how agents collaborate to achieve project goals.
                    </p>
                    <A
                      href={`/projects/${params.id}/workflows/new`}
                      class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-medium transition-all"
                    >
                      Create First Workflow
                    </A>
                  </div>
                </Show>

                <Show when={workflows().length > 0}>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <For each={workflows()}>
                      {(workflow: ProjectWorkflow) => (
                        <div class="bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-all">
                          <div class="p-4">
                            <div class="flex items-start justify-between mb-3">
                              <div class="flex-1 min-w-0">
                                <A
                                  href={`/projects/${params.id}/workflows/${workflow.id}`}
                                  class="text-lg font-medium text-gray-200 hover:text-cyan-400 transition-colors line-clamp-1"
                                >
                                  {workflow.name}
                                </A>
                                <p class="text-gray-500 text-sm mt-1 line-clamp-2">
                                  {workflow.description || 'No description'}
                                </p>
                              </div>
                            </div>

                            <div class="flex flex-wrap gap-2 mb-3">
                              <span
                                class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                                  workflow.status,
                                )}`}
                              >
                                {workflow.status}
                              </span>
                              <For each={workflow.triggers?.slice(0, 2)}>
                                {(trigger) => (
                                  <span
                                    class={`px-2 py-0.5 rounded-full text-xs border ${getTriggerTypeBadge(
                                      trigger.type,
                                    )}`}
                                  >
                                    {trigger.type}
                                  </span>
                                )}
                              </For>
                            </div>

                            <div class="flex items-center gap-4 text-xs text-gray-500">
                              <Show when={workflow.agentsInvolved?.length}>
                                <span>{workflow.agentsInvolved.length} agents</span>
                              </Show>
                              <span>Updated {formatRelativeTime(workflow.updatedAt)}</span>
                            </div>
                          </div>

                          <div class="border-t border-gray-700 px-4 py-2 flex items-center justify-end gap-2">
                            <A
                              href={`/projects/${params.id}/workflows/${workflow.id}`}
                              class="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                            >
                              Edit
                            </A>
                            <button
                              type="button"
                              onClick={() => handleDeleteWorkflow(workflow.id)}
                              class="text-sm text-red-400 hover:text-red-300 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            </Show>

            {/* Agents Tab */}
            <Show when={activeTab() === 'agents'}>
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <p class="text-gray-400">
                    {agents().length} agent{agents().length !== 1 ? 's' : ''} in this project
                  </p>
                  <A
                    href={`/projects/${params.id}/agents/new`}
                    class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="mr-2"
                    >
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                    New Agent
                  </A>
                </div>

                <Show when={agents().length === 0}>
                  <div class="text-center py-12 bg-gray-800/50 rounded-lg border border-gray-700">
                    <svg
                      class="w-16 h-16 mx-auto text-gray-600 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1"
                        d="M9 12h.01M15 12h.01M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1"
                        d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1"
                      />
                    </svg>
                    <h3 class="text-xl font-medium text-gray-300 mb-2">No agents yet</h3>
                    <p class="text-gray-500 mb-6">
                      Create agents to start building your project's capabilities.
                    </p>
                    <A
                      href={`/projects/${params.id}/agents/new`}
                      class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
                    >
                      Create First Agent
                    </A>
                  </div>
                </Show>

                <Show when={agents().length > 0}>
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <For each={agents()}>
                      {(agent) => (
                        <div class="bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-all">
                          <div class="p-4">
                            <div class="flex items-start justify-between mb-3">
                              <div class="flex-1">
                                <A
                                  href={`/projects/${params.id}/agents/${agent.id}/edit`}
                                  class="text-lg font-medium text-gray-200 hover:text-purple-400 transition-colors line-clamp-1"
                                >
                                  {agent.name}
                                </A>
                                <p class="text-gray-500 text-sm mt-1 line-clamp-2">
                                  {agent.purpose || 'No purpose defined'}
                                </p>
                              </div>
                            </div>

                            <div class="flex flex-wrap gap-2 mb-3">
                              <span
                                class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                                  agent.status,
                                )}`}
                              >
                                {agent.status}
                              </span>
                              <span
                                class={`px-2 py-0.5 rounded-full text-xs border ${getVisibilityBadgeClass(
                                  agent.visibility,
                                )}`}
                              >
                                {agent.visibility}
                              </span>
                            </div>

                            <Show when={agent.artifact_types && agent.artifact_types.length > 0}>
                              <div class="mb-3">
                                <span class="text-xs text-gray-500">Outputs:</span>
                                <span class="text-xs text-gray-400">
                                  {agent.artifact_types?.slice(0, 3).join(', ')}
                                  {agent.artifact_types && agent.artifact_types.length > 3 && '...'}
                                </span>
                              </div>
                            </Show>

                            <div class="text-xs text-gray-500">
                              Updated {formatRelativeTime(agent.updated_at)}
                            </div>
                          </div>

                          <div class="border-t border-gray-700 px-4 py-2 flex items-center justify-end gap-2">
                            <A
                              href={`/projects/${params.id}/agents/${agent.id}/edit`}
                              class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                            >
                              Edit
                            </A>
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            </Show>

            {/* Executions Tab */}
            <Show when={activeTab() === 'executions'}>
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-4">
                    <p class="text-gray-400">
                      {executionsData()?.total || 0} execution
                      {executionsData()?.total !== 1 ? 's' : ''}
                    </p>
                    <A
                      href={`/executions?projectId=${params.id}`}
                      class="text-sm text-purple-400 hover:text-purple-300"
                    >
                      View All
                    </A>
                  </div>
                  <Show when={proj().status === 'active'}>
                    <button
                      type="button"
                      onClick={handleRunProject}
                      disabled={isRunning()}
                      class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all disabled:opacity-50"
                    >
                      {isRunning() ? 'Running...' : 'Run Project'}
                    </button>
                  </Show>
                </div>

                <Show when={executions().length === 0}>
                  <div class="text-center py-12 bg-gray-800/50 rounded-lg border border-gray-700">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="mx-auto text-gray-600 mb-4"
                    >
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                    <h3 class="text-xl font-medium text-gray-300 mb-2">No executions yet</h3>
                    <p class="text-gray-500 mb-6">Run this project to see execution history.</p>
                    <Show when={proj().status === 'active'}>
                      <button
                        type="button"
                        onClick={handleRunProject}
                        disabled={isRunning()}
                        class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all disabled:opacity-50"
                      >
                        Run Project
                      </button>
                    </Show>
                  </div>
                </Show>

                <Show when={executions().length > 0}>
                  <div class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
                    <table class="w-full">
                      <thead class="bg-gray-900/50 border-b border-gray-700">
                        <tr>
                          <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">
                            Status
                          </th>
                          <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">
                            Current Step
                          </th>
                          <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">
                            Started
                          </th>
                          <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">
                            Duration
                          </th>
                          <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">
                            Tokens
                          </th>
                          <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">
                            Cost
                          </th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-700">
                        <For each={executions()}>
                          {(execution) => {
                            const duration =
                              execution.completed_at && execution.started_at
                                ? Math.round(
                                    (new Date(execution.completed_at).getTime() -
                                      new Date(execution.started_at).getTime()) /
                                      1000,
                                  )
                                : null;
                            return (
                              <tr
                                class="hover:bg-gray-800/50 transition-colors cursor-pointer"
                                onClick={() => navigate(`/executions/${execution.id}`)}
                              >
                                <td class="px-4 py-3">
                                  <span
                                    class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                                      execution.status,
                                    )}`}
                                  >
                                    {execution.status}
                                  </span>
                                </td>
                                <td class="px-4 py-3">
                                  <p class="text-sm text-gray-300">
                                    {execution.current_step || '-'}
                                  </p>
                                  <Show when={execution.error}>
                                    <p class="text-xs text-red-400 mt-1 truncate max-w-xs">
                                      {execution.error}
                                    </p>
                                  </Show>
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-400">
                                  {execution.started_at
                                    ? formatRelativeTime(execution.started_at)
                                    : formatRelativeTime(execution.created_at)}
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-400">
                                  {duration !== null
                                    ? `${duration}s`
                                    : execution.status === 'running'
                                      ? 'Running...'
                                      : '-'}
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-400">
                                  {execution.tokens_used?.toLocaleString() || '-'}
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-400">
                                  {execution.compute_cost
                                    ? `$${execution.compute_cost.toFixed(4)}`
                                    : '-'}
                                </td>
                              </tr>
                            );
                          }}
                        </For>
                      </tbody>
                    </table>
                  </div>
                </Show>
              </div>
            </Show>

            {/* Interactions Tab */}
            <Show when={activeTab() === 'interactions'}>
              <div class="space-y-6">
                {/* HITL Settings Section */}
                <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <div class="flex items-center justify-between mb-4">
                    <h2 class="text-lg font-medium text-gray-200 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-orange-400"
                      >
                        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      HITL Requirements
                    </h2>
                    <button
                      type="button"
                      onClick={() => setShowAddRequirementForm(!showAddRequirementForm())}
                      class="text-sm text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-1"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M12 5v14" />
                        <path d="M5 12h14" />
                      </svg>
                      Add Requirement
                    </button>
                  </div>

                  <p class="text-gray-500 text-sm mb-4">
                    Configure when agents should request human input during execution. Most of the time,
                    the agent decides when to involve humans based on the trigger condition you set.
                  </p>

                  {/* Add Requirement Form */}
                  <Show when={showAddRequirementForm()}>
                    <div class="bg-gray-900/50 rounded-lg p-4 mb-4 border border-gray-600">
                      <h3 class="text-sm font-medium text-gray-300 mb-3">Add HITL Requirement</h3>
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div class="md:col-span-3">
                          <label class="block text-xs text-gray-400 mb-1">Interaction Type</label>
                          <select
                            value={newRequirementType()}
                            onChange={(e) => setNewRequirementType(e.currentTarget.value)}
                            class="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm focus:outline-none focus:border-orange-500"
                          >
                            <option value="">Select type...</option>
                            <For each={interactionTypes()}>
                              {(type: InteractionType) => (
                                <option value={type.id}>{type.name}</option>
                              )}
                            </For>
                          </select>
                          {/* Interaction Type Explanation */}
                          <Show when={newRequirementType()}>
                            {(() => {
                              const selectedType = interactionTypes().find((t: InteractionType) => t.id === newRequirementType());
                              const slug = selectedType?.slug;
                              return (
                                <div class="mt-2 p-3 bg-gray-800/50 border border-gray-600 rounded-lg">
                                  <div class="text-sm text-gray-300">
                                    {slug === 'approval' && (
                                      <>
                                        <strong class="text-green-400">Approval:</strong> Agent asks human to approve or reject an action. You can add optional feedback to explain your decision.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "Should I proceed with deleting these 50 files?" → Approve/Reject with comment</div>
                                      </>
                                    )}
                                    {slug === 'confirmation' && (
                                      <>
                                        <strong class="text-blue-400">Confirmation:</strong> Simple yes/no confirmation for critical actions. Shows consequences before you decide.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "This will deploy to production. Confirm?" → Confirm/Cancel</div>
                                      </>
                                    )}
                                    {slug === 'multiple-choice' && (
                                      <>
                                        <strong class="text-purple-400">Multiple Choice:</strong> Agent presents several options for you to choose from. Can be single or multi-select.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "Which framework should I use?" → React / Vue / Svelte</div>
                                      </>
                                    )}
                                    {slug === 'free-text' && (
                                      <>
                                        <strong class="text-cyan-400">Free Text Input:</strong> Agent needs open-ended input from you. Use when the agent needs specific information, creative direction, or clarification that can't be predicted.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "What should the error message say?" → You type your answer</div>
                                      </>
                                    )}
                                    {slug === 'discussion' && (
                                      <>
                                        <strong class="text-indigo-400">Discussion Thread:</strong> Back-and-forth conversation with the agent. Use for complex topics requiring multiple exchanges to reach a decision.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "Let's discuss the architecture" → Multi-turn conversation</div>
                                      </>
                                    )}
                                    {slug === 'code-review' && (
                                      <>
                                        <strong class="text-yellow-400">Code Review:</strong> Review code changes before they're applied. Approve, request changes, or add comments.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "Review this refactoring" → Approve / Request Changes / Comment</div>
                                      </>
                                    )}
                                    {slug === 'file-selection' && (
                                      <>
                                        <strong class="text-orange-400">File Selection:</strong> Choose files from a list. Useful when agent needs you to pick specific files to process.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "Which files should I analyze?" → Select from file list</div>
                                      </>
                                    )}
                                    {slug === 'priority-ranking' && (
                                      <>
                                        <strong class="text-pink-400">Priority Ranking:</strong> Rank items by priority. Drag to reorder so agent knows your preferences.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "Rank these features by importance" → Drag to reorder</div>
                                      </>
                                    )}
                                    {slug === 'brainstorm' && (
                                      <>
                                        <strong class="text-emerald-400">Brainstorm:</strong> Collaborative idea generation. Multiple people can contribute ideas over time.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "Ideas for new features?" → Add multiple ideas</div>
                                      </>
                                    )}
                                    {slug === 'inspiration' && (
                                      <>
                                        <strong class="text-violet-400">Creative Inspiration:</strong> Share creative direction, references, or examples to guide the agent's work.
                                        <div class="text-xs text-gray-500 mt-1 italic">e.g., "What style should the UI have?" → Share references and ideas</div>
                                      </>
                                    )}
                                    {!['approval', 'confirmation', 'multiple-choice', 'free-text', 'discussion', 'code-review', 'file-selection', 'priority-ranking', 'brainstorm', 'inspiration'].includes(slug || '') && (
                                      <>
                                        <strong class="text-gray-400">{selectedType?.name || 'Custom'}:</strong> {selectedType?.description || 'Custom interaction type'}
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </Show>
                        </div>
                        <div class="md:col-span-2">
                          <label class="block text-xs text-gray-400 mb-1">When to Trigger</label>
                          <select
                            value={newRequirementTrigger()}
                            onChange={(e) => setNewRequirementTrigger(e.currentTarget.value as typeof newRequirementTrigger extends () => infer T ? T : never)}
                            class="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm focus:outline-none focus:border-orange-500"
                          >
                            <option value="manual">Agent decides (Recommended)</option>
                            <option value="always">Always (every execution)</option>
                            <option value="phase">At specific phase</option>
                            <option value="event">On specific event</option>
                          </select>
                          {/* Trigger Explanation */}
                          <div class="mt-2 p-2 bg-gray-900/30 rounded text-xs text-gray-400">
                            {newRequirementTrigger() === 'manual' && (
                              <>
                                <strong class="text-orange-400">Agent Decides:</strong> The agent determines when to involve you
                                based on context. This is the most flexible option - the agent will request input when it encounters
                                uncertainty, needs approval, or hits a decision point.
                              </>
                            )}
                            {newRequirementTrigger() === 'always' && (
                              <>
                                <strong class="text-blue-400">Always:</strong> Request human input at the start of every execution.
                                Good for mandatory sign-offs or when you want to review the plan before any work begins.
                              </>
                            )}
                            {newRequirementTrigger() === 'phase' && (
                              <>
                                <strong class="text-purple-400">At Phase:</strong> Triggers at a specific workflow phase
                                (e.g., "before deployment", "after testing"). Define the phase in your workflow configuration.
                              </>
                            )}
                            {newRequirementTrigger() === 'event' && (
                              <>
                                <strong class="text-cyan-400">On Event:</strong> Triggers when a specific event occurs
                                (e.g., "error encountered", "budget exceeded", "new requirement added"). Events are raised by
                                the agent during execution.
                              </>
                            )}
                          </div>
                        </div>
                        <div>
                          <label class="block text-xs text-gray-400 mb-1">Priority</label>
                          <select
                            value={newRequirementPriority()}
                            onChange={(e) => setNewRequirementPriority(e.currentTarget.value as typeof newRequirementPriority extends () => infer T ? T : never)}
                            class="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm focus:outline-none focus:border-orange-500"
                          >
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                      </div>
                      <div class="flex justify-end gap-2 mt-4">
                        <button
                          type="button"
                          onClick={() => setShowAddRequirementForm(false)}
                          class="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (newRequirementType()) {
                              await handleAddInteractionRequirement(
                                newRequirementType(),
                                newRequirementTrigger(),
                                newRequirementPriority(),
                              );
                              setNewRequirementType('');
                              setNewRequirementTrigger('manual');
                              setNewRequirementPriority('normal');
                              setShowAddRequirementForm(false);
                            }
                          }}
                          disabled={!newRequirementType()}
                          class="px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </Show>

                  {/* Requirements List */}
                  <Show when={requirementsConfig().length === 0}>
                    <div class="text-center py-6 text-gray-500">
                      <p>No HITL requirements configured yet.</p>
                      <p class="text-sm mt-1">Add requirements to define when agents should request human input.</p>
                    </div>
                  </Show>

                  <Show when={requirementsConfig().length > 0}>
                    <div class="space-y-2">
                      <For each={requirementsConfig()}>
                        {(req: ProjectInteractionRequirement) => (
                          <div class="flex items-center justify-between p-3 rounded-lg bg-gray-900/50 border border-gray-700">
                            <div class="flex items-center gap-3">
                              <span class="text-gray-200 font-medium">
                                {req.interactionType?.name || req.interactionTypeId}
                              </span>
                              <span class="px-2 py-0.5 rounded-full text-xs bg-gray-700 text-gray-300">
                                {req.triggerType}
                              </span>
                              <span
                                class={`px-2 py-0.5 rounded-full text-xs border ${
                                  req.priority === 'urgent'
                                    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                    : req.priority === 'high'
                                      ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                                      : req.priority === 'normal'
                                        ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                        : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                                }`}
                              >
                                {req.priority}
                              </span>
                              <Show when={req.required}>
                                <span class="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
                                  required
                                </span>
                              </Show>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteInteractionRequirement(req.id)}
                              class="text-gray-400 hover:text-red-400 transition-colors"
                              title="Delete requirement"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              >
                                <path d="M3 6h18" />
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </For>
                    </div>
                  </Show>
                </section>

                {/* Recent Interactions Section */}
                <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <div class="flex items-center justify-between mb-4">
                    <h2 class="text-lg font-medium text-gray-200 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-purple-400"
                      >
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      Recent Interactions ({interactions().length})
                    </h2>
                    <A
                      href="/interactions"
                      class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      View all
                    </A>
                  </div>

                  <Show when={interactions().length === 0}>
                    <div class="text-center py-8 text-gray-500">
                      <p>No interactions yet.</p>
                      <p class="text-sm mt-1">Interactions will appear here when agents need human input.</p>
                    </div>
                  </Show>

                  <Show when={interactions().length > 0}>
                    <div class="space-y-3">
                      <For each={interactions()}>
                        {(interaction: Interaction) => (
                          <A
                            href={`/interactions?id=${interaction.id}`}
                            class="block bg-gray-900/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-all p-4"
                          >
                            <div class="flex items-start justify-between">
                              <div class="flex-1 min-w-0">
                                <div class="flex items-center gap-3">
                                  <span class="text-gray-200 font-medium truncate">
                                    {interaction.title}
                                  </span>
                                  <span
                                    class={`px-2 py-0.5 rounded-full text-xs border ${
                                      interaction.status === 'pending'
                                        ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                                        : interaction.status === 'resolved'
                                          ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                          : interaction.status === 'cancelled'
                                            ? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                                            : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                    }`}
                                  >
                                    {interaction.status}
                                  </span>
                                  <span
                                    class={`px-2 py-0.5 rounded-full text-xs border ${
                                      interaction.priority === 'urgent'
                                        ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                        : interaction.priority === 'high'
                                          ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                                          : interaction.priority === 'normal'
                                            ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                            : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                                    }`}
                                  >
                                    {interaction.priority}
                                  </span>
                                </div>
                                <Show when={interaction.description}>
                                  <p class="text-gray-500 text-sm mt-1 line-clamp-2">
                                    {interaction.description}
                                  </p>
                                </Show>
                                <div class="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                  <span class="capitalize">{interaction.interactionTypeId}</span>
                                  <span>Created {formatRelativeTime(interaction.createdAt)}</span>
                                </div>
                              </div>
                              <div class="ml-4 text-gray-400">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                >
                                  <path d="m9 18 6-6-6-6" />
                                </svg>
                              </div>
                            </div>
                          </A>
                        )}
                      </For>
                    </div>
                  </Show>
                </section>
              </div>
            </Show>

            {/* Artifacts Tab */}
            <Show when={activeTab() === 'artifacts'}>
              <div class="space-y-6">
                <div class="flex items-center justify-between">
                  <div>
                    <h2 class="text-lg font-medium text-gray-200">Project Artifacts</h2>
                    <p class="text-sm text-gray-500 mt-1">
                      Outputs produced by this project's agent executions
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => refetchArtifacts()}
                    class="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm transition-colors"
                  >
                    Refresh
                  </button>
                </div>

                {/* Loading State */}
                <Show when={artifactsData.loading}>
                  <div class="flex items-center justify-center py-12">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
                  </div>
                </Show>

                {/* Empty State */}
                <Show when={!artifactsData.loading && artifacts().length === 0}>
                  <div class="text-center py-12 bg-gray-800/50 rounded-lg border border-gray-700">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="mx-auto text-gray-600 mb-4"
                    >
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      <polyline points="3.29 7 12 12 20.71 7" />
                      <line x1="12" y1="22" x2="12" y2="12" />
                    </svg>
                    <h3 class="text-gray-400 font-medium mb-2">No artifacts yet</h3>
                    <p class="text-gray-500 text-sm">
                      Run the project to generate artifacts like dashboards, reports, and datasets.
                    </p>
                  </div>
                </Show>

                {/* Artifacts Grid */}
                <Show when={!artifactsData.loading && artifacts().length > 0}>
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <For each={artifacts()}>
                      {(artifact: Artifact) => (
                        <A
                          href={`/artifacts/${artifact.id}`}
                          class="block p-4 rounded-lg bg-gray-800 border border-gray-700 hover:border-purple-500/50 transition-colors"
                        >
                          <div class="flex items-start justify-between mb-3">
                            <div class="flex items-center gap-2">
                              <span class="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400 capitalize">
                                {artifact.type}
                              </span>
                              <Show when={artifact.version_num && artifact.version_num > 1}>
                                <span class="px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-400">
                                  v{artifact.version_num}
                                </span>
                              </Show>
                            </div>
                            <span class="text-xs text-gray-500">
                              {artifact.visibility}
                            </span>
                          </div>
                          <h3 class="font-medium text-gray-200 mb-1 truncate">
                            {artifact.title}
                          </h3>
                          <Show when={artifact.description}>
                            <p class="text-sm text-gray-400 line-clamp-2 mb-3">
                              {artifact.description}
                            </p>
                          </Show>
                          <div class="text-xs text-gray-500">
                            Updated {new Date(artifact.updated_at).toLocaleDateString()}
                          </div>
                        </A>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            </Show>

            {/* Files Tab */}
            <Show when={activeTab() === 'files'}>
              <div class="space-y-6">
                <div class="flex items-center justify-between">
                  <div>
                    <h2 class="text-lg font-medium text-gray-200">Project Files</h2>
                    <p class="text-sm text-gray-500 mt-1">
                      Upload documents, data files, and references for this project
                    </p>
                  </div>
                  <span class="px-2.5 py-1 rounded-lg bg-gray-800 text-sm text-gray-400">
                    {files().length} file{files().length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* File Uploader */}
                <FileUploader
                  context="project"
                  contextId={params.id}
                  onUploadComplete={() => refetchFiles()}
                  onError={(error) => console.error('Upload error:', error)}
                />

                {/* File List */}
                <FileList
                  files={files()}
                  loading={filesData.loading}
                  onRefresh={() => refetchFiles()}
                  emptyMessage="No files uploaded yet. Drag & drop files above to add them to this project."
                />
              </div>
            </Show>
          </>
        )}
      </Show>
    </div>
  );
};

export default ProjectDetail;
